import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";

import type { Activity } from "@/api/queries";
import { useAgenda } from "@/api/queries";
import { useMe } from "@/auth/useMe";
import { DetalleEvento } from "@/agenda/DetalleEvento";
import type { Density, Scheduled } from "@/agenda/model";
import {
  BANDS,
  CHANNEL_BAR,
  OUT_OF_HOURS,
  addDays,
  addMonths,
  bandOf,
  channelOf,
  dayKey,
  eventsOn,
  hasOutOfHours,
  isSameDay,
  isScheduled,
  rangeFor,
  startOfMonth,
  startOfWeek,
} from "@/agenda/model";
import {
  Button,
  EmptyState,
  ErrorState,
  Page,
  Seg,
  Select,
  Spinner,
  Tag,
} from "@/components/ui";
import { NuevoSeguimiento } from "@/actividades/NuevoSeguimiento";
import { useAgents } from "@/leads/queries";

/**
 * Screen 03 of the Modernist design — the agenda (.docs/08 §7 F4).
 *
 * The design draws Week and only Week. Day and Month are derived from it: the
 * same events at three densities, from one request each, so nothing about what
 * an event *is* differs between them. What differs is how much of a week you
 * can see at once, which is the only thing a density is for.
 *
 * Colour is the **channel** and never a funnel stage (D24) — go out, dial, or
 * write — keyed to the legacy `Activity.Kind` integers so a relabelled kind
 * cannot silently change what a column means. The legend is not decoration:
 * a mono grid with three greys is unreadable without it.
 *
 * Below `sm` the week grid is replaced by a list grouped by day, and Day is
 * the default. Five columns of five bands do not survive 390px in any honest
 * form, so .docs/08 §6 draws that phone artboard separately rather than
 * pretending the grid reflows.
 */

const DENSITIES: Density[] = ["day", "week", "month"];

function parseDensity(raw: string | null): Density {
  return DENSITIES.includes(raw as Density) ? (raw as Density) : "week";
}

/** The anchor day, off the URL so a week can be linked to. */
function parseAnchor(raw: string | null): Date {
  if (raw) {
    const parsed = new Date(`${raw}T12:00`);
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }
  return new Date();
}

export function Agenda() {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const [params, setParams] = useSearchParams();
  const [openEvent, setOpenEvent] = useState<Activity | null>(null);
  const [scheduling, setScheduling] = useState(false);

  const isManager = me?.is_manager ?? false;
  const density = parseDensity(params.get("v"));
  const anchor = parseAnchor(params.get("d"));
  const agent = params.get("agent") ?? "";
  // `mine` unless a manager has asked for the team. The server honours `all`
  // for managers only, so this is a view preference and not a privilege claim.
  const team = isManager && params.get("equipo") === "1";

  const range = useMemo(() => rangeFor(density, anchor), [density, anchor]);
  const agenda = useAgenda({
    from: range.from,
    to: range.to,
    scope: team ? "all" : "mine",
    ...(isManager && agent ? { agent } : {}),
  });
  const agents = useAgents(isManager);

  /**
   * One call per change, however many keys it touches.
   *
   * Two `setParams` in a row do not compose: React batches them and the second
   * updater reads the params as they were before the first, so switching to
   * the team view and picking a seller in the same gesture silently dropped
   * the first half of it.
   */
  function update(changes: Record<string, string>) {
    setParams((previous) => {
      const next = new URLSearchParams(previous);
      for (const [key, value] of Object.entries(changes)) {
        if (value) next.set(key, value);
        else next.delete(key);
      }
      return next;
    });
  }

  function move(direction: 1 | -1) {
    const next =
      density === "day"
        ? addDays(anchor, direction)
        : density === "week"
          ? addDays(startOfWeek(anchor), direction * 7)
          : addMonths(anchor, direction);
    update({ d: dayKey(next) });
  }

  // Dated work only, which is all the request asks for: `due_at` is empty on
  // what a call or an email logged as done, and that is never on an agenda.
  const events = (agenda.data?.results ?? []).filter(isScheduled);
  const todayCount = eventsOn(events, new Date()).length;

  return (
    <Page
      title={t("agenda.title")}
      subtitle={t("agenda.count", { today: todayCount, range: events.length })}
      actions={
        <Button variant="primary" onClick={() => setScheduling(true)}>
          {t("agenda.schedule")}
        </Button>
      }
    >
      <div className="mb-5 flex flex-wrap items-center gap-2.5">
        <Seg<Density>
          name="agenda-density"
          value={density}
          onChange={(value) => update({ v: value })}
          options={DENSITIES.map((value) => ({
            value,
            label: t(`agenda.${value}`),
          }))}
        />

        <div className="flex items-center gap-1.5">
          <Button onClick={() => move(-1)}>{t("agenda.previous")}</Button>
          <Button onClick={() => update({ d: "" })}>{t("agenda.today")}</Button>
          <Button onClick={() => move(1)}>{t("agenda.next")}</Button>
        </div>

        <span className="font-display text-sm font-extrabold">
          <RangeLabel density={density} anchor={anchor} />
        </span>

        {/* Manager-only, and says so. An affordance that appears for some
            accounts and not others reads as a bug unless the reason is on
            screen — which is what the design's "Gerencia" tag is for. */}
        {isManager ? (
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <Select
              value={team ? agent || "team" : "mine"}
              aria-label={t("agenda.filterAgent")}
              className="w-auto"
              onChange={(event) => {
                const value = event.target.value;
                if (value === "mine") {
                  update({ equipo: "", agent: "" });
                  return;
                }
                update({ equipo: "1", agent: value === "team" ? "" : value });
              }}
            >
              <option value="mine">{t("agenda.mine")}</option>
              <option value="team">{t("agenda.wholeTeam")}</option>
              {(agents.data ?? []).map((option) => (
                <option key={option.id} value={String(option.id)}>
                  {option.name || `#${option.id}`}
                </option>
              ))}
            </Select>
            <Tag
              tone="outline"
              className="text-[10px] tracking-[0.08em] uppercase"
              title={t("agenda.managerOnly")}
            >
              {t("agenda.manager")}
            </Tag>
          </div>
        ) : null}
      </div>

      {agenda.isPending ? (
        <Spinner label={t("agenda.loading")} />
      ) : agenda.isError ? (
        <ErrorState
          message={t("agenda.loadFailed")}
          onRetry={() => void agenda.refetch()}
        />
      ) : (
        <>
          {density === "day" ? (
            <DayList events={eventsOn(events, anchor)} onOpen={setOpenEvent} />
          ) : density === "month" ? (
            <MonthGrid
              anchor={anchor}
              events={events}
              onPickDay={(day) => update({ v: "day", d: dayKey(day) })}
            />
          ) : (
            <>
              {/* Two renderings of the same week, one per width. The grid
                  cannot be reflowed into the list — five columns of five bands
                  do not survive 390px — so .docs/08 §6 draws them as separate
                  artboards and they are built as separate markup. */}
              <div className="hidden sm:block">
                <WeekGrid anchor={anchor} events={events} onOpen={setOpenEvent} />
              </div>
              <div className="sm:hidden">
                <WeekList anchor={anchor} events={events} onOpen={setOpenEvent} />
              </div>
            </>
          )}

          <Legend />
        </>
      )}

      <DetalleEvento event={openEvent} onClose={() => setOpenEvent(null)} />
      {scheduling ? (
        <NuevoSeguimiento open onClose={() => setScheduling(false)} />
      ) : null}
    </Page>
  );
}

/* ── Pieces ──────────────────────────────────────────────────────────────── */

const LONG_DATE = new Intl.DateTimeFormat("es-MX", {
  day: "numeric",
  month: "long",
  year: "numeric",
});
const SHORT_DATE = new Intl.DateTimeFormat("es-MX", { day: "numeric", month: "short" });
const WEEKDAY = new Intl.DateTimeFormat("es-MX", { weekday: "short" });
const MONTH = new Intl.DateTimeFormat("es-MX", { month: "long", year: "numeric" });
const TIME = new Intl.DateTimeFormat("es-MX", { hour: "2-digit", minute: "2-digit" });

function RangeLabel({ density, anchor }: { density: Density; anchor: Date }) {
  if (density === "day") return <>{LONG_DATE.format(anchor)}</>;
  if (density === "month") return <>{MONTH.format(anchor)}</>;
  const monday = startOfWeek(anchor);
  return (
    <>
      {SHORT_DATE.format(monday)} – {SHORT_DATE.format(addDays(monday, 4))}
    </>
  );
}

function EventCard({
  event,
  onOpen,
  compact = false,
}: {
  event: Scheduled;
  onOpen: (event: Activity) => void;
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const done = event.is_completed ?? false;
  return (
    <button
      type="button"
      onClick={() => onOpen(event)}
      aria-label={`${t("agenda.openEvent")}: ${event.kind_display}`}
      className="flex w-full flex-col gap-0.5 border-l-[3px] bg-panel px-2.5 py-2 text-left text-xs hover:shadow-sm"
      style={{ borderLeftColor: CHANNEL_BAR[channelOf(event.kind)] }}
    >
      <span className="flex items-center justify-between gap-1.5">
        <strong className={done ? "line-through" : ""}>{event.kind_display}</strong>
        <StateMark done={done} />
      </span>
      {compact ? null : (
        <span className="truncate opacity-70">
          {event.company_name ?? t("agenda.noCompany")}
        </span>
      )}
      <span className="text-[11px] tabular-nums opacity-50">
        {TIME.format(new Date(event.due_at))}
      </span>
    </button>
  );
}

function StateMark({ done }: { done: boolean }) {
  const { t } = useTranslation();
  const label = t(done ? "agenda.done" : "agenda.pending");
  return (
    <span
      title={label}
      aria-label={label}
      role="img"
      className={`inline-flex h-[15px] w-[15px] shrink-0 items-center justify-center border-2 text-[9px] leading-none ${
        done ? "border-brand-700 text-brand-700" : "border-mute-600"
      }`}
    >
      {done ? "✓" : ""}
    </span>
  );
}

/** The design's grid: a 72px time gutter and five working-day columns. */
function WeekGrid({
  anchor,
  events,
  onOpen,
}: {
  anchor: Date;
  events: Scheduled[];
  onOpen: (event: Activity) => void;
}) {
  const { t } = useTranslation();
  const monday = startOfWeek(anchor);
  const days = [0, 1, 2, 3, 4].map((offset) => addDays(monday, offset));
  const bands: number[] = [...BANDS];
  if (hasOutOfHours(events)) bands.push(OUT_OF_HOURS);

  return (
    <div className="grid grid-cols-[72px_repeat(5,1fr)] border-t-2 border-rule">
      <div className="border-r border-rule" />
      {days.map((day) => {
        const today = isSameDay(day, new Date());
        return (
          <div
            key={dayKey(day)}
            className={`border-r border-b-2 border-rule px-3.5 py-3 last:border-r-0 ${
              today ? "bg-brand text-ground" : ""
            }`}
          >
            <span className="font-display text-[15px] font-extrabold">
              {WEEKDAY.format(day)} {day.getDate()}
              {today ? ` · ${t("agenda.today").toLowerCase()}` : ""}
            </span>
            <div className={`text-[11px] ${today ? "opacity-85" : "opacity-55"}`}>
              {t("agenda.events", { count: eventsOn(events, day).length })}
            </div>
          </div>
        );
      })}

      {bands.map((band) => (
        <BandRow key={band} band={band} days={days} events={events} onOpen={onOpen} />
      ))}
    </div>
  );
}

function BandRow({
  band,
  days,
  events,
  onOpen,
}: {
  band: number;
  days: Date[];
  events: Scheduled[];
  onOpen: (event: Activity) => void;
}) {
  const { t } = useTranslation();
  return (
    <>
      <div className="border-r border-b border-rule px-2.5 py-1.5 text-right text-[11px] text-mute-600">
        {band === OUT_OF_HOURS
          ? t("agenda.outOfHours")
          : `${String(band).padStart(2, "0")}:00`}
      </div>
      {days.map((day) => {
        const cell = eventsOn(events, day).filter(
          (event) => bandOf(new Date(event.due_at)) === band,
        );
        return (
          <div
            key={`${band}-${dayKey(day)}`}
            // `min-h`, not a fixed height: the artboard shows one card per
            // cell and a real Tuesday at 13:00 can hold three. Clipping them
            // would hide work; growing the row does not.
            className="flex min-h-[88px] flex-col gap-1 border-r border-b border-rule p-1.5 last:border-r-0"
          >
            {cell.map((event) => (
              <EventCard key={event.id} event={event} onOpen={onOpen} />
            ))}
          </div>
        );
      })}
    </>
  );
}

/** The phone's week (artboard 03b): the same days, as a grouped list. */
function WeekList({
  anchor,
  events,
  onOpen,
}: {
  anchor: Date;
  events: Scheduled[];
  onOpen: (event: Activity) => void;
}) {
  const monday = startOfWeek(anchor);
  return (
    <div className="border-t-2 border-rule">
      {[0, 1, 2, 3, 4].map((offset) => (
        <DayGroup
          key={offset}
          day={addDays(monday, offset)}
          events={events}
          onOpen={onOpen}
        />
      ))}
    </div>
  );
}

function DayGroup({
  day,
  events,
  onOpen,
}: {
  day: Date;
  events: Scheduled[];
  onOpen: (event: Activity) => void;
}) {
  const { t } = useTranslation();
  const rows = eventsOn(events, day);
  const today = isSameDay(day, new Date());
  return (
    <section>
      {/* Sticky, because the count is what a grid says at a glance and a list
          loses the moment the heading scrolls away (.docs/08 §6). */}
      <header
        className={`sticky top-0 z-[1] flex items-baseline gap-2.5 border-b border-rule px-4 py-2.5 ${
          today ? "bg-brand text-ground" : "bg-panel"
        }`}
      >
        <span className="font-display text-sm font-extrabold">
          {WEEKDAY.format(day)} {day.getDate()}
          {today ? ` · ${t("agenda.today").toLowerCase()}` : ""}
        </span>
        <span className={`ml-auto text-[11px] ${today ? "opacity-80" : "opacity-55"}`}>
          {t("agenda.events", { count: rows.length })}
        </span>
      </header>
      {rows.map((event) => (
        <div key={event.id} className="border-b border-rule px-4 py-2">
          <EventCard event={event} onOpen={onOpen} />
        </div>
      ))}
    </section>
  );
}

function DayList({
  events,
  onOpen,
}: {
  events: Scheduled[];
  onOpen: (event: Activity) => void;
}) {
  const { t } = useTranslation();
  if (events.length === 0) return <EmptyState>{t("agenda.emptyDay")}</EmptyState>;
  return (
    <div className="border-t-2 border-rule">
      {events.map((event) => (
        <div
          key={event.id}
          className="grid grid-cols-[56px_1fr] items-start gap-3 border-b border-rule py-3"
        >
          <span className="pt-2 font-display text-sm font-extrabold tabular-nums">
            {TIME.format(new Date(event.due_at))}
          </span>
          <EventCard event={event} onOpen={onOpen} />
        </div>
      ))}
    </div>
  );
}

/**
 * The third density, which the design does not draw.
 *
 * A month of cards would be a scroll with no shape, so this is a calendar of
 * counts: what a month is actually read for is "which days are busy". Picking
 * one switches to Day, where the events themselves are.
 */
function MonthGrid({
  anchor,
  events,
  onPickDay,
}: {
  anchor: Date;
  events: Scheduled[];
  onPickDay: (day: Date) => void;
}) {
  const { t } = useTranslation();
  const first = startOfMonth(anchor);
  const gridStart = startOfWeek(first);
  const cells = Array.from({ length: 42 }, (_, index) => addDays(gridStart, index));
  const weekdays = [0, 1, 2, 3, 4, 5, 6].map((offset) => addDays(gridStart, offset));

  return (
    <div className="overflow-x-auto">
      <div className="grid min-w-[560px] grid-cols-7 border-t-2 border-l border-rule">
        {weekdays.map((day) => (
          <div
            key={`head-${day.getDay()}`}
            className="border-r border-b-2 border-rule px-2 py-2 text-[11px] tracking-[0.06em] text-mute-600 uppercase"
          >
            {WEEKDAY.format(day)}
          </div>
        ))}
        {cells.map((day) => {
          const rows = eventsOn(events, day);
          const outside = day.getMonth() !== first.getMonth();
          const today = isSameDay(day, new Date());
          return (
            <button
              key={dayKey(day)}
              type="button"
              onClick={() => onPickDay(day)}
              className={`min-h-[84px] border-r border-b border-rule p-2 text-left ${
                outside ? "opacity-40" : ""
              } ${today ? "bg-brand text-ground" : "hover:bg-mute-200"}`}
            >
              <span className="font-display text-sm font-extrabold">
                {day.getDate()}
              </span>
              {rows.length > 0 ? (
                <span className="mt-1 block text-[11px] opacity-70">
                  {t("agenda.events", { count: rows.length })}
                </span>
              ) : null}
              <span className="mt-1.5 flex flex-wrap gap-1">
                {rows.slice(0, 6).map((event) => (
                  <span
                    key={event.id}
                    aria-hidden="true"
                    className="h-1.5 w-1.5"
                    style={{ background: CHANNEL_BAR[channelOf(event.kind)] }}
                  />
                ))}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Not optional. Three greys on a mono grid are unreadable without it, and the
 * design puts it under every artboard for that reason (.docs/08 §7 F4).
 */
function Legend() {
  const { t } = useTranslation();
  return (
    <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2.5 border-t border-rule pt-4 text-xs">
      <span className="text-mute-600">{t("agenda.channel")}</span>
      {(["visit", "call", "message"] as const).map((channel) => (
        <span key={channel} className="flex items-center gap-2">
          <span
            aria-hidden="true"
            className="h-3.5 w-3.5"
            style={{ background: CHANNEL_BAR[channel] }}
          />
          {t(
            channel === "visit"
              ? "agenda.channelVisit"
              : channel === "call"
                ? "agenda.channelCall"
                : "agenda.channelMessage",
          )}
        </span>
      ))}
      {/* Pending only: done work is not on the agenda (see `useAgenda`), so a
          key for it would describe a mark the grid never draws. */}
      <span className="flex items-center gap-2 sm:ml-6 sm:border-l-2 sm:border-rule sm:pl-6">
        <StateMark done={false} />
        {t("agenda.pending")}
      </span>
    </div>
  );
}
